import React, { useState, useEffect, useMemo } from 'react';
import { useGuests, useAttendanceMutation } from '../api';
import { getFloorplanUrl } from './PhotoGallery/utils/photoHelpers';
import ImageModal from './ImageModal';


const Floorplan = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGuest, setSelectedGuest] = useState(null);
  const [attendanceMessage, setAttendanceMessage] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Use React Query hooks for data fetching
  const {
    data: guests = [],
    isLoading: guestsLoading,
    error: guestsError
  } = useGuests();

  const attendanceMutation = useAttendanceMutation();

  // Combined loading and error states
  const loading = guestsLoading;
  const error = guestsError;

  // Helper function to extract guest data consistently
  const getGuestData = (guest) => ({
    name: guest.name || guest.guest_name || '',
    table: guest.table_number || guest.table || ''
  });

  // Guest attendance updates are now handled automatically by React Query mutations

  // SVG Icon Constants
  const CheckIcon = () => (
    <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
    </svg>
  );

  const FloorplanIcon = () => (
    <svg className="mx-auto h-16 w-16 text-wedding-sage/40 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
    </svg>
  );

  // Reusable component for floorplan unavailable state
  const FloorplanUnavailable = ({ title = "Floorplan Coming Soon", message = "The venue floorplan will be available closer to the wedding date." }) => (
    <div className="bg-white rounded-xl shadow-sm border border-wedding-sage/20 p-6 sm:p-8 text-center">
      <FloorplanIcon />
      <h3 className="text-lg font-medium text-wedding-dark-brown mb-2">
        {title}
      </h3>
      <p className="text-wedding-dark-brown/70">
        {message}
      </p>
    </div>
  );

  // Handle authentication errors
  useEffect(() => {
    if (guestsError?.response?.status === 403) {
      localStorage.removeItem('weddingToken');
      setTimeout(() => {
        window.location.href = '/';
      }, 2000);
    }
  }, [guestsError]);

  // Get floorplan URL from photoHelpers utility
  const floorplanUrl = getFloorplanUrl();

  // Optimized search functionality using useMemo
  const filteredGuestsList = useMemo(() => {
    if (!searchTerm.trim()) {
      return guests;
    }

    const searchLower = searchTerm.toLowerCase();
    return guests.filter(guest => {
      const { name, table } = getGuestData(guest);

      return name.toLowerCase().includes(searchLower) ||
             table.toString().includes(searchLower);
    });
  }, [searchTerm, guests]);

  // Handle guest selection
  const handleGuestSelect = (guest) => {
    setSelectedGuest(guest);
    setSearchTerm('');
  };

  // Handle attendance registration using React Query mutation
  const handleAttendance = async (attended) => {
    if (!selectedGuest) return;

    setAttendanceMessage('');

    const { name: guestName, table: tableNumber } = getGuestData(selectedGuest);

    try {
      await attendanceMutation.mutateAsync({
        guestName,
        tableNumber,
        attended
      });

      setAttendanceMessage(
        attended
          ? `✅ Attendance confirmed for ${guestName}!`
          : `❌ Attendance cancelled for ${guestName}.`
      );

      // Update selectedGuest state for instant UI feedback
      setSelectedGuest(prev => prev ? { ...prev, attended: attended ? 'Y' : 'N' } : null);

      // Clear message after 3 seconds
      setTimeout(() => setAttendanceMessage(''), 3000);

    } catch (err) {
      console.error('Attendance error:', err);
      setAttendanceMessage('❌ Failed to update attendance. Please try again.');
      setTimeout(() => setAttendanceMessage(''), 5000);
    }
  };

  // Clear search and selection
  const clearSearch = () => {
    setSearchTerm('');
    setSelectedGuest(null);
  };

  // Modal handlers
  const openFloorplanModal = () => {
    setIsModalOpen(true);
  };

  const closeFloorplanModal = () => {
    setIsModalOpen(false);
  };


  // Skeleton loading component
  const SkeletonLoader = () => (
    <div className="w-full bg-gradient-to-b from-wedding-sage-hint/20 to-wedding-sage-hint/10 min-h-screen">
      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Header Skeleton */}
        <div className="text-center mb-8">
          <div className="h-8 bg-wedding-sage/20 rounded-lg w-64 mx-auto mb-2 animate-pulse"></div>
          <div className="h-4 bg-wedding-sage/10 rounded w-48 mx-auto animate-pulse"></div>
        </div>

        {/* Search Section Skeleton */}
        <div className="bg-white rounded-xl shadow-sm border border-wedding-sage/20 p-4 sm:p-6 mb-6">
          <div className="space-y-4">
            {/* Search Label */}
            <div className="h-4 bg-wedding-sage/10 rounded w-48 animate-pulse"></div>

            {/* Search Input Skeleton */}
            <div className="relative">
              <div className="w-full h-12 bg-wedding-sage/10 rounded-lg animate-pulse"></div>
            </div>
          </div>
        </div>

        {/* Floorplan Display Skeleton */}
        <div className="bg-white rounded-xl shadow-sm border border-wedding-sage/20 p-4 sm:p-6">
          <div className="h-6 bg-wedding-sage/20 rounded w-32 mb-4 animate-pulse"></div>
          <div className="aspect-video bg-wedding-sage/10 rounded-lg animate-pulse"></div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return <SkeletonLoader />;
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-4">
        <div className="text-center max-w-md">
          <svg className="mx-auto h-16 w-16 text-red-500 mb-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" />
          </svg>
          <h2 className="text-xl font-semibold text-wedding-dark-brown mb-3">Error Loading Floorplan</h2>
          <p className="text-wedding-dark-brown/70 mb-6 leading-relaxed">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="inline-flex items-center space-x-2 px-6 py-3 bg-wedding-sage text-white rounded-lg hover:bg-wedding-sage/90 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            <span>Try Again</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full bg-gradient-to-b from-wedding-sage-hint/20 to-wedding-sage-hint/10">
      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-2">
            <h1 className="text-2xl font-semibold text-wedding-dark-brown">
              Wedding Floorplan
            </h1>
          </div>
          <p className="text-wedding-dark-brown/70">
            Find your table and confirm your attendance.
          </p>
          <p className="text-wedding-dark-brown/60 text-sm mt-1">
            If you are having issues, please proceed to the reception and ask for assistance.
          </p>
        </div>

        {/* Search Section */}
        <div className="bg-white rounded-xl shadow-sm border border-wedding-sage/20 p-4 sm:p-6 mb-6">
          <div className="space-y-4">
            <label htmlFor="guest-search" className="block text-sm font-medium text-wedding-dark-brown">
              Search for your name or table number
            </label>

            {/* Search Input Container */}
            <div className="relative">
              <input
                id="guest-search"
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Enter your name or table number..."
                className={`w-full px-4 py-3 border border-wedding-sage/30 rounded-lg focus:ring-2 focus:ring-wedding-sage/50 focus:border-wedding-sage transition-colors ${
                  (searchTerm || selectedGuest) ? 'pr-12' : 'pr-10'
                }`}
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center space-x-2">
                {/* Search Icon - always visible */}
                <svg className="w-5 h-5 text-wedding-sage/60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>

                {/* Clear Button - only visible when there's content */}
                {(searchTerm || selectedGuest) && (
                  <button
                    onClick={clearSearch}
                    className="p-1 text-wedding-sage/60 hover:text-wedding-sage hover:bg-wedding-sage/10 rounded transition-colors"
                    aria-label="Clear search"
                    title="Clear search"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Search Results */}
          {searchTerm && (
            <div className="mt-4">
              <h3 className="text-sm font-medium text-wedding-dark-brown mb-2">
                Search Results ({filteredGuestsList.length})
              </h3>
              <div className="max-h-80 sm:max-h-48 overflow-y-auto border border-wedding-sage/20 rounded-lg">
                {filteredGuestsList.length > 0 ? (
                  filteredGuestsList.map((guest) => (
                    <button
                      key={guest.id}
                      onClick={() => handleGuestSelect(guest)}
                      className="w-full text-left px-4 py-3 hover:bg-wedding-sage/5 border-b border-wedding-sage/10 last:border-b-0 transition-colors"
                    >
                      <div className="flex justify-between items-center">
                        <div className="flex items-center space-x-3">
                          <span className="font-medium text-wedding-dark-brown">
                            {getGuestData(guest).name}
                          </span>
                          {/* Table Number Badge */}
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-wedding-sage text-white border border-wedding-sage shadow-sm">
                            Table {getGuestData(guest).table}
                          </span>
                        </div>
                        {/* Attendance Status Badge */}
                        <div className="flex items-center">
                          {guest.attended === 'Y' ? (
                            <span className="text-wedding-white text-sm font-medium bg-wedding-sage-hint px-2 py-1 rounded-full border border-wedding-sage flex items-center">
                              <CheckIcon />
                              Confirmed
                            </span>
                          ) : guest.attended === 'N' ? (
                            <span className="text-wedding-dark-brown text-sm font-medium bg-wedding-light-brown/20 px-2 py-1 rounded-full border border-wedding-dark-brown flex items-center">
                              <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                              </svg>
                              Check-In
                            </span>
                          ) : (
                            <span className="text-wedding-dark-brown/60 text-sm font-medium bg-gray-100 px-2 py-1 rounded-full border border-gray-300 flex items-center">
                              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              Not Registered
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  ))
                ) : (
                  <div className="px-4 py-3 text-wedding-dark-brown/60 text-center">
                    No guests found matching "{searchTerm}"
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Selected Guest */}
          {selectedGuest && !searchTerm && (
            <div className="mt-4 p-4 bg-wedding-sage/10 border border-wedding-sage/30 rounded-lg">
              <div className="flex items-center justify-between gap-3">
                {/* Guest Info */}
                <div className="flex items-center space-x-3 flex-1 min-w-0">
                  <div className="min-w-0 flex-1">
                    <h3 className="font-medium text-wedding-dark-brown truncate">
                      {getGuestData(selectedGuest).name}
                    </h3>
                    {selectedGuest.attended === 'Y' && (
                      <p className="text-xs text-wedding-sage font-medium flex items-center mt-0.5">
                        <CheckIcon />
                        Confirmed
                      </p>
                    )}
                  </div>
                  {/* Table Number Badge */}
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-wedding-sage text-white border border-wedding-sage shadow-sm flex-shrink-0">
                    Table {getGuestData(selectedGuest).table}
                  </span>
                </div>

                {/* Descriptive Action Button */}
                <div className="flex-shrink-0">
                  <button
                    onClick={() => handleAttendance(selectedGuest.attended !== 'Y')}
                    disabled={attendanceMutation.isPending}
                    className={`inline-flex items-center justify-center px-3 py-2 rounded-lg font-medium text-sm transition-all duration-200 shadow-sm ${
                      selectedGuest.attended === 'Y'
                        ? 'bg-wedding-sage text-white border-2 border-wedding-dark-sage hover:bg-wedding-dark-brown'
                        : 'bg-white text-wedding-sage border-2 border-wedding-sage hover:bg-wedding-sage hover:text-white'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                    title={selectedGuest.attended === 'Y' ? "You're marked as attending - tap to remove attendance" : "Mark yourself as attending the wedding"}
                    aria-label={selectedGuest.attended === 'Y' ? "Remove attendance confirmation" : "Confirm attendance"}
                  >
                    {attendanceMutation.isPending ? (
                      <>
                        <svg className="w-4 h-4 mr-2 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span>Updating...</span>
                      </>
                    ) : selectedGuest.attended === 'Y' ? (
                      <>
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>Confirmed!</span>
                      </>
                    ) : (
                      <>
                        <span>Mark as Attending</span>
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Compact Message Display */}
              {attendanceMessage && (
                <div className="mt-3 p-2 bg-white/80 rounded text-xs">
                  {attendanceMessage}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Floorplan Display */}
        {floorplanUrl ? (
          <div className="bg-white rounded-xl shadow-sm border border-wedding-sage/20 p-4 sm:p-6">
            <h2 className="text-lg font-medium text-wedding-dark-brown mb-4 text-center">
              Venue Floorplan
            </h2>
            <div className="flex justify-center">
              <div className="relative group cursor-pointer" onClick={openFloorplanModal}>
                <img
                  src={floorplanUrl}
                  alt="Wedding venue floorplan"
                  className="max-w-full h-auto rounded-lg shadow-md hover:shadow-lg transition-all duration-200"
                  loading="lazy"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'block';
                  }}
                  title="Click to view full-size floorplan"
                />

                {/* Click to enlarge overlay */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 rounded-lg transition-all duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100 pointer-events-none">
                  <div className="bg-white/90 backdrop-blur-sm rounded-full p-3 shadow-lg">
                    <svg className="w-6 h-6 text-wedding-dark-brown" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                    </svg>
                  </div>
                </div>

                {/* Subtle hint text */}
                <div className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                  <div className="bg-black/60 backdrop-blur-sm text-white text-xs px-2 py-1 rounded">
                    Click to enlarge
                  </div>
                </div>
              </div>
              <div className="hidden">
                <FloorplanUnavailable
                  title="Floorplan Not Available"
                  message="Floorplan image not available"
                />
              </div>
            </div>
          </div>
        ) : (
          <FloorplanUnavailable />
        )}

        {/* Floorplan Modal */}
        <ImageModal
          isOpen={isModalOpen}
          onClose={closeFloorplanModal}
          imageUrl={floorplanUrl}
          imageAlt="Wedding venue floorplan - full size"
          title="Wedding Venue Floorplan"
        />
      </div>
    </div>
  );
};

export default Floorplan;
